import React, {createElement, FC, Fragment} from 'react';
import {BlogSignUp} from 'components/blog/BlogSignUp';
import {BookingWidget} from 'components/ContentPagesComponents/BookingWidget';
import {DynamicWidget} from 'components/ContentPagesComponents/DynamicWidget/Loadable';
import {BlogPricing} from 'components/ContentPagesComponents/PricingList';
import {TrackLink} from 'components/Markdown/Link';
import {DivTable} from 'components/Markdown/Table';
import {GatsbyImage, IGatsbyImageData} from 'gatsby-plugin-image';
import rehypeRaw from 'rehype-raw';
import rehypeReact from 'rehype-react';
import remarkBreaks from 'remark-breaks';
import remarkCustomHeaderId from 'remark-custom-header-id';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkLint from 'remark-preset-lint-recommended';
import remarkRehype from 'remark-rehype';
import {unified} from 'unified';

import {Map} from './Map';
import remarkCustomVoids from './remarkCustomVoids';

type Image = {
	alt: string
	image: IGatsbyImageData
}

export type ContentProps = {
	classNames?: {
		wrapper?: string
		image?: string
		table?: string
	}
	markdown: string
	images: Image[]
}

const components = (images: Image[], imgClassName?: string) => {
	const components: {[k: string]: FC} = {
		'x-booking': (props: object) => <BookingWidget {...props} />,
		'x-hint': () => <BlogSignUp responsive />,
		'x-map': (props: object) => <Map zoom='10' {...props} />,
		'x-pricing': () => <BlogPricing />,
		'x-widget': (props: object) => <DynamicWidget {...props} mode='render' />,
	};

	images.forEach((c, i) => {
		components[`x-image-${i + 1}`] = () => (
			<GatsbyImage className={imgClassName} {...c} />
		);
	});

	return components;
};

const trimInvalids = (markdown: string) => {
	return markdown
		.split('\n')
		.map((line) => line.trimEnd())
		.map((line) => line.replace(/^ +<x-/, '<x-'))
		.join('\n');
};

export const Content: FC<ContentProps> = ({classNames, markdown, images}) => {
	const content = unified()
		.use(remarkParse)
		.use(remarkLint)
		.use(remarkBreaks)
		.use(remarkCustomVoids)
		.use(remarkGfm)
		.use(remarkCustomHeaderId)
		.use(remarkRehype, {allowDangerousHtml: true})
		.use(rehypeRaw)
		.use(rehypeReact, {
			...{Fragment, createElement},
			components: {
				...components(images, classNames?.image),
				a: TrackLink('blog:text'),
				table: DivTable(classNames?.table),
			},
		})
		.processSync(trimInvalids(markdown)).result;

	return (
		<div className={classNames?.wrapper}>
			{content}
		</div>
	);
};
